import React, { useState } from 'react';
import { Award, Loader2, X, CheckCircle2, XCircle, Sparkles, TrendingUp } from 'lucide-react';
import { Button } from './ui/button';
import { authFetch } from '../utils/authUtils';

/**
 * Full-screen quiz modal.
 * Flow: Generate → Take → Submit → See results.
 */
const QuizModal = ({ contentId, contentTitle, onClose }) => {
  const [quizId, setQuizId] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  const generate = async () => {
    if (!contentId) {
      setError('Missing content id');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await authFetch(
        `${BACKEND_URL}/api/quiz/generate/${contentId}`,
        { method: 'POST' },
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Quiz generation failed');
      setQuizId(data.quiz_id);
      setQuestions(data.questions || []);
      setAnswers({});
      setResults(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const submit = async () => {
    if (!quizId) return;
    // Convert answers dict → array in question order
    const answerArray = questions.map((_, i) => answers[i] || null);
    setSubmitting(true);
    setError(null);
    try {
      const response = await authFetch(
        `${BACKEND_URL}/api/quiz/${quizId}/submit`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ answers: answerArray }),
        },
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Quiz submission failed');
      setResults(data);
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('coins-changed'));
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const allAnswered = questions.length > 0 && questions.every((_, i) => answers[i]);

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto"
      data-testid="quiz-modal"
    >
      <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-3xl my-8 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-700/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-teal-600 rounded-lg flex items-center justify-center">
              <Award className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">AI Comprehension Quiz</h2>
              {contentTitle && (
                <p className="text-xs text-gray-400">on "{contentTitle}"</p>
              )}
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="text-gray-400 hover:text-white"
            data-testid="quiz-close"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Body */}
        <div className="p-6 max-h-[70vh] overflow-y-auto space-y-4">
          {!questions.length && !loading && !error && (
            <div className="text-center py-10">
              <p className="text-gray-300 mb-4">
                Test your understanding with 5 AI-generated questions.
              </p>
              <Button
                onClick={generate}
                className="bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700 text-white"
                data-testid="quiz-generate"
              >
                <Award className="w-4 h-4 mr-2" />
                Generate Quiz
              </Button>
            </div>
          )}

          {loading && (
            <div className="flex items-center justify-center gap-3 py-16" data-testid="quiz-loading">
              <Loader2 className="w-6 h-6 text-green-400 animate-spin" />
              <p className="text-gray-300">Generating questions…</p>
            </div>
          )}

          {error && (
            <div className="p-4 bg-red-900/30 border border-red-700 rounded-lg" data-testid="quiz-error">
              <p className="text-red-300 text-sm">{error}</p>
              <Button
                onClick={generate}
                size="sm"
                variant="outline"
                className="mt-3 border-red-600 text-red-300 hover:bg-red-900/30"
              >
                Retry
              </Button>
            </div>
          )}

          {/* Quiz questions */}
          {questions.length > 0 && !results && (
            <div className="space-y-6">
              {questions.map((q, qi) => (
                <div
                  key={qi}
                  className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/40"
                  data-testid={`quiz-question-${qi}`}
                >
                  <p className="text-white font-medium mb-3">
                    <span className="text-green-400 mr-2">Q{qi + 1}.</span>
                    {q.question}
                  </p>
                  <div className="space-y-2">
                    {q.options.map((opt, oi) => {
                      const letter = opt.trim().charAt(0).toUpperCase();
                      const selected = answers[qi] === letter;
                      return (
                        <button
                          key={oi}
                          onClick={() => setAnswers({ ...answers, [qi]: letter })}
                          className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors border ${
                            selected
                              ? 'bg-indigo-600/30 border-indigo-500 text-white'
                              : 'bg-slate-900/50 border-slate-700 text-gray-300 hover:border-slate-500'
                          }`}
                          data-testid={`quiz-option-${qi}-${letter}`}
                        >
                          {opt}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Results view */}
          {results && (
            <div data-testid="quiz-results">
              <div className="text-center mb-6 p-6 bg-gradient-to-br from-green-900/30 to-teal-900/30 rounded-xl border border-green-700/40">
                <p className="text-sm text-gray-400">Your score</p>
                <p className="text-5xl font-bold text-white my-2">
                  {Math.round(results.score)}%
                </p>
                <p className="text-gray-300">
                  {results.correct_count} / {results.total_questions} correct
                </p>
                {results.is_perfect && (
                  <p className="mt-3 text-sm font-semibold text-yellow-300" data-testid="quiz-perfect-badge">
                    🏆 Perfect score! +30 XP bonus
                  </p>
                )}
              </div>

              {/* XP awarded */}
              {results.xp_awarded && Object.values(results.xp_awarded).some((v) => v > 0) && (
                <div
                  className="mb-4 p-4 bg-indigo-900/30 border border-indigo-700/40 rounded-lg"
                  data-testid="quiz-xp-card"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-indigo-300" />
                      <p className="text-sm font-semibold text-indigo-200">XP earned</p>
                    </div>
                    {results.xp_multiplier && results.xp_multiplier > 1 && (
                      <span
                        className="px-2 py-0.5 rounded-full text-xs font-bold bg-gradient-to-r from-amber-500 to-orange-500 text-white"
                        data-testid="quiz-xp-multiplier"
                      >
                        {results.xp_multiplier}× multiplier
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2 text-xs">
                    {Object.entries(results.xp_awarded)
                      .filter(([, v]) => v > 0)
                      .map(([stat, v]) => (
                        <span
                          key={stat}
                          className="px-2 py-1 rounded-full bg-indigo-800/40 border border-indigo-600/40 text-indigo-100"
                        >
                          +{v} {stat.replace(/_/g, ' ')}
                        </span>
                      ))}
                  </div>
                </div>
              )}

              {/* Level ups */}
              {results.level_ups && results.level_ups.length > 0 && (
                <div
                  className="mb-4 p-4 bg-gradient-to-r from-yellow-600/20 to-amber-600/20 border border-yellow-500/40 rounded-lg"
                  data-testid="quiz-level-up"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="w-4 h-4 text-yellow-300" />
                    <p className="text-sm font-semibold text-yellow-200">Level up!</p>
                  </div>
                  <ul className="text-xs text-yellow-100/90 space-y-0.5">
                    {results.level_ups.map((lu, idx) => (
                      <li key={idx}>
                        {lu.stat?.replace(/_/g, ' ')} → level {lu.new_level}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="space-y-4">
                {results.results.map((r, i) => (
                  <div
                    key={i}
                    className={`p-4 rounded-lg border ${
                      r.is_correct
                        ? 'bg-green-900/20 border-green-700/40'
                        : 'bg-red-900/20 border-red-700/40'
                    }`}
                    data-testid={`quiz-result-${i}`}
                  >
                    <div className="flex items-start gap-2 mb-2">
                      {r.is_correct ? (
                        <CheckCircle2 className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                      )}
                      <p className="text-white text-sm font-medium">{r.question}</p>
                    </div>
                    <p className="text-xs text-gray-300 ml-7">
                      Your answer: <span className="font-mono">{r.user_answer || '—'}</span>
                      {!r.is_correct && (
                        <>
                          {' • '}Correct:{' '}
                          <span className="font-mono text-green-300">{r.correct_answer}</span>
                        </>
                      )}
                    </p>
                    {r.explanation && (
                      <p className="text-xs text-gray-400 ml-7 mt-1 italic">
                        {r.explanation}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {questions.length > 0 && !results && (
          <div className="flex justify-end gap-2 p-5 border-t border-slate-700/50">
            <Button
              onClick={onClose}
              variant="outline"
              className="border-slate-600 text-gray-300 hover:bg-slate-700"
            >
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={!allAnswered || submitting}
              className="bg-green-600 hover:bg-green-700 disabled:opacity-50"
              data-testid="quiz-submit"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting…
                </>
              ) : (
                <>Submit Answers</>
              )}
            </Button>
          </div>
        )}
        {results && (
          <div className="flex justify-end gap-2 p-5 border-t border-slate-700/50">
            <Button
              onClick={generate}
              variant="outline"
              className="border-indigo-600 text-indigo-300 hover:bg-indigo-900/30"
              data-testid="quiz-retry"
            >
              Regenerate
            </Button>
            <Button
              onClick={onClose}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              Done
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuizModal;
